import swal from 'sweetalert'
import $ from 'jquery'

/**
 * Modales de sweetalert del sistema: la alerta de un botón y la confirmación de dos.
 *
 * Documentación de la pieza: `docs/design-system/modal-confirmacion.md`.
 */

/** Los mismos iconos que los botones Guardar / Cancelar del pie de los ABM. */
export const ICONO_CONFIRMAR = 'fa fa-check-circle';
export const ICONO_CANCELAR = 'fa fa-times-circle';

/**
 * Arma el objeto `buttons` de una confirmación. Se exporta aparte para poder testear el patrón:
 * el ORDEN de las claves es lo que define de qué lado cae cada botón, porque sweetalert los
 * renderiza en el orden en que están declarados.
 *
 * Confirmar a la izquierda y Cancelar en rojo a la derecha es el patrón dominante del sistema
 * (63 confirmaciones escritas a mano lo siguen, contra 5 con el orden inverso).
 */
export const botonesDeConfirmacion = (confirmText, cancelText) => ({
	confirm: {
		text: confirmText,
		value: true,
		visible: true,
		className: 'btn btn-primary',
		closeModal: true,
	},
	cancel: {
		text: cancelText,
		value: false,
		visible: true,
		className: 'btn btn-danger',
		closeModal: true,
	},
});

/**
 * El único botón de una alerta. Vive afuera por el mismo motivo que `botonesDeConfirmacion`:
 * para poder testear el contrato sin renderizar.
 *
 * Lleva el MISMO icono que el Confirmar de las confirmaciones y que los botones Guardar del pie
 * de los ABM (`ICONO_CONFIRMAR`), que es lo que lo hace parte de la misma familia visual: hasta
 * VEC-3572 las alertas eran los únicos swal del sistema con el botón pelado.
 */
export const botonDeAceptar = (okText) => ({
	confirm: {
		text: okText,
		value: true,
		visible: true,
		className: 'btn btn-primary',
		closeModal: true,
	},
});

/**
 * sweetalert escapa el `text` de los botones, así que los iconos se agregan al DOM una vez
 * abierto el modal. Se buscan por las clases que declara `botonesDeConfirmacion`, porque la clase
 * propia de sweetalert (`swal-button--confirm`) se arma dinámicamente.
 */
export const agregarIconos = (intentos) => {
	const restantes = intentos === undefined ? 3 : intentos;

	const poner = (selector, icono) => {
		const boton = $(selector);
		if (boton.length && !boton.find('i').length) {
			boton.prepend('<i class="' + icono + '"></i> ');
		}
		return boton.length;
	};

	const listos = poner('.swal-footer .btn-primary', ICONO_CONFIRMAR) && poner('.swal-footer .btn-danger', ICONO_CANCELAR);

	if (!listos && restantes > 0) {
		setTimeout(() => agregarIconos(restantes - 1), 0);
	}
};

class Dialog {
	static alert(intl,options) {
		// *** LA KEY ES NUEVA A PROPÓSITO. ***
		// Antes era `dialog.errors.button_aceptar` con defaultMessage 'Aceptar'. Esa key ya está en
		// el catálogo remoto (flow.vecfleet.io/api/lang), así que cambiarle sólo el defaultMessage
		// no habría cambiado nada en pantalla: el catálogo gana. Renombrarla hace que caiga al
		// defaultMessage de acá hasta que se cargue la traducción.
		const okText = intl.formatMessage({ id: 'dialog.button.ok', defaultMessage: 'Ok' });

		swal({
			title: options && options.title ? options.title : intl.formatMessage({ id: 'dialog.errors.error_interno_del_sistema.', defaultMessage: 'Error interno del sistema.' }),
			text: options && options.text ? options.text : intl.formatMessage({ id: 'dialog.errors.contacte_al_administrador', defaultMessage: 'Contacte al administrador.' }),
			icon: options && options.icon ? options.icon : "error",
			buttons: botonDeAceptar(okText),
		});

		// El icono se inyecta después de abrir, igual que en `confirm`. Ojo con un detalle de
		// `agregarIconos`: se considera "lista" sólo cuando encuentra los DOS botones, y una alerta
		// tiene uno, así que reintenta sus 3 veces. Es inocuo —`poner` no duplica el <i> si ya
		// está— pero explica los timeouts.
		agregarIconos();
	}

	/**
	 * Confirmación simple. Admite las dos formas de consumo:
	 *   Dialog.confirm(intl, options, onConfirm, onCancel)      // callbacks
	 *   Dialog.confirm(intl, options).then((confirmado) => …)   // promesa, resuelve booleano
	 *
	 * `confirmado` es siempre booleano: `false` tanto si canceló como si cerró el modal, así el
	 * caso "no confirmó" no queda sin manejar.
	 */
	static confirm(intl, options, onConfirm, onCancel) {
		const confirmText = options && options.confirmText
			? options.confirmText
			: intl.formatMessage({ id: 'dialog.button.aceptar', defaultMessage: 'Aceptar' });
		const cancelText = options && options.cancelText
			? options.cancelText
			: intl.formatMessage({ id: 'dialog.button.cancelar', defaultMessage: 'Cancelar' });

		const promesa = swal({
			title: options && options.title ? options.title : '',
			text: options && options.text ? options.text : '',
			icon: options && options.icon ? options.icon : 'warning',
			buttons: botonesDeConfirmacion(confirmText, cancelText),
		}).then((valor) => {
			const confirmado = !!valor;
			if (confirmado && onConfirm) {
				onConfirm();
			} else if (!confirmado && onCancel) {
				onCancel();
			}
			return confirmado;
		});

		agregarIconos();

		return promesa;
	}
}

export default Dialog;
